import { createSlice, type PayloadAction } from '@reduxjs/toolkit';
import {
  classifyReplayMessage,
  normalizeChatInitMessages,
} from '@/utils/chatMessages';
import type {
  CallMessage,
  TranscriptChunk,
  TranscriptSegmentNote,
  RecordingState,
} from '@/types/call';
import type { CustomerInformationResponse } from '@/types/customer';
import type {
  CaseInformationResponse,
  CaseHistoryEntry,
  CasePrefillFields,
} from '@/types/case';

/**
 * Normalize buffered transcripts: one row per segment (partials with same resultId → last partial; final with sentenceId replaces last partial).
 */
function normalizeTranscriptChunks(
  chunks: TranscriptChunk[]
): TranscriptChunk[] {
  const result: TranscriptChunk[] = [];
  for (const chunk of chunks) {
    if (chunk.isPartial && chunk.resultId) {
      const idx = result.findIndex(c => c.resultId === chunk.resultId);
      if (idx >= 0) result[idx] = chunk;
      else result.push(chunk);
    } else if (!chunk.isPartial && chunk.sentenceId) {
      const last = result[result.length - 1];
      if (last?.isPartial) result[result.length - 1] = chunk;
      else result.push(chunk);
    } else if (!chunk.isPartial && chunk.resultId) {
      const idx = result.findIndex(c => c.resultId === chunk.resultId);
      if (idx >= 0) result[idx] = chunk;
      else result.push(chunk);
    } else {
      result.push(chunk);
    }
  }
  return result;
}

export type ContactSessionType = 'voice' | 'chat' | 'unknown';

export interface ContactSession {
  contactId: string | null;
  type: ContactSessionType;
  /** True while opening stream/chat init before first init payload arrives. */
  isStreamInitializing: boolean;
  /** True while the SSE transcript stream is open and receiving events. */
  streamActive: boolean;
  previousMessages: CallMessage[];
  /** Stable client-side session start timestamp in ms. */
  startedAt: number | null;
  callerPhoneNumber: string | null;
  callerName: string | null;
  intentName: string | null;
  /** Client-provided alert message from init variables.clientAlert. */
  clientAlert: string | null;
  /** Authentication status of the customer from Boost metadata. */
  authenticated: boolean | null;
  /** When true, this call is a queue callback. Voice only. */
  isCallback: boolean | null;
  /** Inbound direct connect: no PII until workspace is linked via company search. */
  isDirectConnect: boolean;
  /** Set after successful PUT /call/:contactId/link-customer (workspace id). */
  directConnectLinkedWorkspaceId: string | null;
  /** Display name from company search when linked without loaded customer PII. */
  directConnectLinkedCompanyName: string | null;
  /** Transcript chunks in order (buffered + live). One row per segment; partials update in place, final replaces last partial. */
  transcriptChunks: TranscriptChunk[];
  /** Notes added to live segments (keyed by sentenceId). Persisted when call ends. */
  segmentNotes: Record<string, TranscriptSegmentNote[]>;
  /** Current recording state for the active call. */
  recordingState: RecordingState | null;
  /** Customer leg on hold (manual hold or warm transfer); survives warm-transfer modal reset. */
  customerOnHold: boolean;
  /** Timestamp (ms) when the customer leg was put on hold; null when not on hold. */
  customerOnHoldSince: number | null;
  /** User-visible warm transfer failure (shown on contact center). */
  warmTransferAlert: string | null;
  /** ChatJS connection token sent to GET /chat/init. */
  chatConnectionToken: string | null;
  /** Snapshot of init history used for order-based Connect replay dedup. */
  replayReference: CallMessage[];
  /** Next index in replayReference to match. */
  replayCursor: number;
  /** True once a live message stops matching replayReference — everything after is new. */
  replayDone: boolean;
  /** messageIds already received (replay + appended) so double-subscription is idempotent. */
  seenMessageIds: string[];
  /** Contact information fetched early for incoming contacts (before stream init). */
  contactInformation: {
    callerName?: string;
    callerPhoneNumber?: string;
    authenticated?: boolean;
    isDirectConnect?: boolean;
    isCallback?: boolean;
  } | null;
  unreadCount: number;
  currentCustomer: CustomerInformationResponse | null;
  currentCase: CaseInformationResponse | null;
  caseHistory: CaseHistoryEntry[];
  casePrefill: CasePrefillFields | null;
}

/**
 * Call context for the current focused contact.
 * The top-level fields are kept as a compatibility mirror for existing cards.
 * `sessions` stores the isolated state for every active call/chat contact.
 */
interface CallState extends Omit<ContactSession, 'type' | 'unreadCount'> {
  focusedContactId: string | null;
  voiceContactId: string | null;
  sessionOrder: string[];
  sessions: Record<string, ContactSession>;
}

const createEmptySession = (
  contactId: string | null,
  type: ContactSessionType = 'unknown'
): ContactSession => ({
  contactId,
  type,
  isStreamInitializing: false,
  streamActive: false,
  previousMessages: [],
  startedAt: null,
  callerPhoneNumber: null,
  callerName: null,
  intentName: null,
  clientAlert: null,
  authenticated: null,
  isCallback: null,
  isDirectConnect: false,
  directConnectLinkedWorkspaceId: null,
  directConnectLinkedCompanyName: null,
  transcriptChunks: [],
  segmentNotes: {},
  recordingState: null,
  customerOnHold: false,
  customerOnHoldSince: null,
  warmTransferAlert: null,
  chatConnectionToken: null,
  replayReference: [],
  replayCursor: 0,
  replayDone: false,
  seenMessageIds: [],
  contactInformation: null,
  unreadCount: 0,
  currentCustomer: null,
  currentCase: null,
  caseHistory: [],
  casePrefill: null,
});

const initialSession = createEmptySession(null);

const initialState: CallState = {
  ...initialSession,
  focusedContactId: null,
  voiceContactId: null,
  sessionOrder: [],
  sessions: {},
};

function copySessionToFocusedState(
  state: CallState,
  session: ContactSession | null
): void {
  const next = session ?? initialSession;
  state.contactId = next.contactId;
  state.isStreamInitializing = next.isStreamInitializing;
  state.previousMessages = next.previousMessages;
  state.startedAt = next.startedAt;
  state.callerPhoneNumber = next.callerPhoneNumber;
  state.callerName = next.callerName;
  state.intentName = next.intentName;
  state.clientAlert = next.clientAlert;
  state.authenticated = next.authenticated;
  state.isCallback = next.isCallback;
  state.isDirectConnect = next.isDirectConnect;
  state.directConnectLinkedWorkspaceId = next.directConnectLinkedWorkspaceId;
  state.directConnectLinkedCompanyName = next.directConnectLinkedCompanyName;
  state.transcriptChunks = next.transcriptChunks;
  state.segmentNotes = next.segmentNotes;
  state.recordingState = next.recordingState;
  state.customerOnHold = next.customerOnHold;
  state.customerOnHoldSince = next.customerOnHoldSince;
  state.warmTransferAlert = next.warmTransferAlert;
  state.chatConnectionToken = next.chatConnectionToken;
  state.contactInformation = next.contactInformation;
  state.currentCustomer = next.currentCustomer;
  state.currentCase = next.currentCase;
  state.caseHistory = next.caseHistory;
  state.casePrefill = next.casePrefill;
}

function ensureSession(
  state: CallState,
  contactId: string,
  type: ContactSessionType = 'unknown',
  startedAt?: number | null
): ContactSession {
  if (!state.sessions[contactId]) {
    state.sessions[contactId] = createEmptySession(contactId, type);
    state.sessions[contactId].startedAt = startedAt ?? null;
    state.sessionOrder.push(contactId);
  } else if (type !== 'unknown') {
    state.sessions[contactId].type = type;
  }
  if (startedAt && !state.sessions[contactId].startedAt) {
    state.sessions[contactId].startedAt = startedAt;
  }
  if (type === 'voice') {
    state.voiceContactId = contactId;
  }
  return state.sessions[contactId];
}

function getTargetContactId(
  state: CallState,
  contactId?: string | null
): string | null {
  return contactId ?? state.focusedContactId ?? state.contactId;
}

function syncFocusedState(state: CallState): void {
  if (state.focusedContactId && state.sessions[state.focusedContactId]) {
    copySessionToFocusedState(state, state.sessions[state.focusedContactId]);
    return;
  }

  const nextId =
    (state.voiceContactId && state.sessions[state.voiceContactId]
      ? state.voiceContactId
      : null) ??
    state.sessionOrder.find(contactId => Boolean(state.sessions[contactId])) ??
    null;

  state.focusedContactId = nextId;
  copySessionToFocusedState(
    nextId ? state : state,
    nextId ? state.sessions[nextId] : null
  );
}

const callSlice = createSlice({
  name: 'call',
  initialState,
  reducers: {
    /** Set state from SSE init event (previous messages, caller info, buffered transcripts). */
    setCallStreamInit: (
      state,
      action: PayloadAction<{
        contactId: string;
        type?: ContactSessionType;
        focus?: boolean;
        startedAt?: number | null;
        previousMessages: CallMessage[];
        callerPhoneNumber: string;
        callerName: string;
        intentName: string;
        clientAlert?: string;
        authenticated?: boolean;
        isCallback?: boolean;
        bufferedTranscripts: TranscriptChunk[];
        recordingState: RecordingState;
        isDirectConnect?: boolean;
        caseHistory?: CaseHistoryEntry[];
        casePrefill?: CasePrefillFields | null;
      }>
    ) => {
      const { contactId } = action.payload;
      // Don't recreate a session that was already deleted (e.g. agent clicked
      // Finish before an in-flight chat/stream init resolved). setCallStreamLoading
      // always creates the session first, so a missing session here means it was
      // intentionally removed.
      if (!state.sessions[contactId]) {
        return;
      }
      const session = state.sessions[contactId];
      const isDirectConnect = Boolean(action.payload.isDirectConnect);
      session.isStreamInitializing = false;
      session.streamActive = true;
      session.previousMessages = action.payload.previousMessages ?? [];
      session.replayReference = [...session.previousMessages];
      session.replayCursor = 0;
      session.replayDone = false;
      session.seenMessageIds = [];
      session.callerPhoneNumber = action.payload.callerPhoneNumber ?? null;
      session.callerName = action.payload.callerName ?? null;
      session.intentName = action.payload.intentName ?? null;
      session.clientAlert = action.payload.clientAlert ?? null;
      if (action.payload.authenticated !== undefined) {
        session.authenticated = action.payload.authenticated;
      }
      session.isCallback = action.payload.isCallback ?? null;
      session.isDirectConnect = isDirectConnect;
      session.directConnectLinkedWorkspaceId = null;
      session.directConnectLinkedCompanyName = null;
      session.caseHistory = action.payload.caseHistory ?? [];
      session.casePrefill = action.payload.casePrefill ?? null;
      session.transcriptChunks = normalizeTranscriptChunks(
        action.payload.bufferedTranscripts ?? []
      );
      session.recordingState = action.payload.recordingState ?? null;
      if (action.payload.focus !== false) {
        state.focusedContactId = action.payload.contactId;
        session.unreadCount = 0;
      }
      syncFocusedState(state);
    },
    /** Mark stream/chat init as started and clear previous call context. */
    setCallStreamLoading: (
      state,
      action: PayloadAction<{
        contactId: string;
        connectionToken?: string;
        type?: ContactSessionType;
        focus?: boolean;
        startedAt?: number | null;
      }>
    ) => {
      const session = ensureSession(
        state,
        action.payload.contactId,
        action.payload.type,
        action.payload.startedAt
      );
      session.startedAt = session.startedAt ?? action.payload.startedAt ?? null;
      session.chatConnectionToken = action.payload.connectionToken ?? null;
      session.isStreamInitializing = true;
      session.streamActive = false;
      session.previousMessages = [];
      session.replayReference = [];
      session.replayCursor = 0;
      session.replayDone = false;
      session.seenMessageIds = [];
      session.callerPhoneNumber = null;
      session.callerName = null;
      session.intentName = null;
      session.clientAlert = null;
      session.isCallback = null;
      session.isDirectConnect = false;
      session.directConnectLinkedWorkspaceId = null;
      session.directConnectLinkedCompanyName = null;
      session.transcriptChunks = [];
      session.segmentNotes = {};
      session.recordingState = null;
      session.customerOnHold = false;
      session.customerOnHoldSince = null;
      session.warmTransferAlert = null;
      session.currentCustomer = null;
      session.currentCase = null;
      session.caseHistory = [];
      session.casePrefill = null;
      if (action.payload.focus !== false) {
        state.focusedContactId = action.payload.contactId;
        session.unreadCount = 0;
      }
      syncFocusedState(state);
    },
    /** Stop stream/chat init loader when init failed or completed elsewhere. */
    clearCallStreamLoading: (
      state,
      action: PayloadAction<{ contactId?: string } | undefined>
    ) => {
      const contactId = getTargetContactId(state, action.payload?.contactId);
      if (contactId && state.sessions[contactId]) {
        state.sessions[contactId].isStreamInitializing = false;
        state.sessions[contactId].streamActive = false;
      }
      syncFocusedState(state);
    },
    /** After successful link-customer; enables downstream customer/case fetch. */
    setDirectConnectLinkedWorkspace: (
      state,
      action: PayloadAction<{ workspaceId: string; companyName?: string }>
    ) => {
      const contactId = getTargetContactId(state);
      if (contactId) {
        const session = ensureSession(state, contactId);
        session.directConnectLinkedWorkspaceId = action.payload.workspaceId;
        session.directConnectLinkedCompanyName =
          action.payload.companyName ?? null;
      }
      syncFocusedState(state);
    },
    /**
     * Append or update transcript chunk.
     * - Partials: one line per resultId; update in place so the line enhances as new words arrive.
     * - Final with sentenceId: finalize the current line (replace last partial so it becomes colored), then next partial starts a new row. If no partial before, append.
     * - Final with resultId only: update that segment to final in place.
     */
    appendTranscriptChunk: (
      state,
      action: PayloadAction<TranscriptChunk & { contactId?: string }>
    ) => {
      const { contactId: payloadContactId, ...chunk } = action.payload;
      const contactId = getTargetContactId(state, payloadContactId);
      if (!contactId) return;
      const session = ensureSession(state, contactId, 'voice');
      if (chunk.isPartial && chunk.resultId) {
        const idx = session.transcriptChunks.findIndex(
          c => c.resultId === chunk.resultId
        );
        if (idx >= 0) {
          session.transcriptChunks[idx] = chunk;
        } else {
          session.transcriptChunks.push(chunk);
        }
        syncFocusedState(state);
        return;
      }
      if (!chunk.isPartial && chunk.sentenceId) {
        const last =
          session.transcriptChunks[session.transcriptChunks.length - 1];
        if (last?.isPartial) {
          session.transcriptChunks[session.transcriptChunks.length - 1] = chunk;
        } else {
          session.transcriptChunks.push(chunk);
        }
        syncFocusedState(state);
        return;
      }
      if (!chunk.isPartial && chunk.resultId) {
        const idx = session.transcriptChunks.findIndex(
          c => c.resultId === chunk.resultId
        );
        if (idx >= 0) {
          session.transcriptChunks[idx] = chunk;
        } else {
          session.transcriptChunks.push(chunk);
        }
        syncFocusedState(state);
        return;
      }
      session.transcriptChunks.push(chunk);
      syncFocusedState(state);
    },
    /** Add a note to a live segment (sentenceId). Shown until call ends. */
    addSegmentNote: (
      state,
      action: PayloadAction<{
        contactId?: string;
        sentenceId: string;
        note: TranscriptSegmentNote;
      }>
    ) => {
      const { contactId: payloadContactId, sentenceId, note } = action.payload;
      const contactId = getTargetContactId(state, payloadContactId);
      if (!contactId) return;
      const session = ensureSession(state, contactId);
      if (!session.segmentNotes[sentenceId])
        session.segmentNotes[sentenceId] = [];
      session.segmentNotes[sentenceId].push(note);
      syncFocusedState(state);
    },
    setRecordingState: (
      state,
      action: PayloadAction<
        | RecordingState
        | null
        | { contactId?: string; recordingState: RecordingState | null }
      >
    ) => {
      const payload = action.payload;
      const contactId =
        typeof payload === 'object' &&
        payload !== null &&
        'recordingState' in payload
          ? getTargetContactId(state, payload.contactId)
          : getTargetContactId(state);
      if (!contactId) return;
      const session = ensureSession(state, contactId);
      session.recordingState =
        typeof payload === 'object' &&
        payload !== null &&
        'recordingState' in payload
          ? payload.recordingState
          : payload;
      syncFocusedState(state);
    },
    setCustomerOnHold: (
      state,
      action: PayloadAction<boolean | { contactId?: string; value: boolean }>
    ) => {
      const payload = action.payload;
      const contactId =
        typeof payload === 'object'
          ? getTargetContactId(state, payload.contactId)
          : getTargetContactId(state);
      if (!contactId) return;
      const session = ensureSession(state, contactId);
      const nextValue = typeof payload === 'object' ? payload.value : payload;
      if (nextValue && !session.customerOnHold) {
        // Hold just started — stamp the start time for the hold timer.
        session.customerOnHoldSince = Date.now();
      } else if (!nextValue) {
        // Resumed (or never held) — no hold in progress.
        session.customerOnHoldSince = null;
      }
      // nextValue true while already on hold keeps the original timestamp
      // (a re-asserted hold is the same continuous hold).
      session.customerOnHold = nextValue;
      syncFocusedState(state);
    },
    setWarmTransferAlert: (state, action: PayloadAction<string | null>) => {
      const contactId = getTargetContactId(state, state.voiceContactId);
      if (!contactId) return;
      const session = ensureSession(state, contactId);
      session.warmTransferAlert = action.payload;
      syncFocusedState(state);
    },
    /** Set contactId and clear transcript state (e.g. when accepting a chat contact). */
    setContactId: (
      state,
      action: PayloadAction<{ contactId: string; type?: ContactSessionType }>
    ) => {
      const session = ensureSession(
        state,
        action.payload.contactId,
        action.payload.type
      );
      session.isStreamInitializing = true;
      session.streamActive = false;
      session.previousMessages = [];
      session.replayReference = [];
      session.replayCursor = 0;
      session.replayDone = false;
      session.seenMessageIds = [];
      session.transcriptChunks = [];
      session.segmentNotes = {};
      session.recordingState = null;
      session.isDirectConnect = false;
      session.directConnectLinkedWorkspaceId = null;
      session.directConnectLinkedCompanyName = null;
      session.intentName = null;
      session.clientAlert = null;
      state.focusedContactId = action.payload.contactId;
      session.unreadCount = 0;
      syncFocusedState(state);
    },
    /** Append one chat message (from Connect onMessage). */
    appendChatMessage: (
      state,
      action: PayloadAction<CallMessage & { contactId?: string }>
    ) => {
      const { contactId: payloadContactId, ...msg } = action.payload;
      const contactId = getTargetContactId(state, payloadContactId);
      if (!contactId) return;
      const session = ensureSession(state, contactId, 'chat');

      // Idempotency: both chat subscriptions (background + viewed) deliver the
      // same Connect event. Exact id only — never body text.
      if (msg.messageId && session.seenMessageIds.includes(msg.messageId)) {
        return;
      }

      // Order-based replay dedup against the init-history snapshot.
      if (!session.replayDone) {
        const decision = classifyReplayMessage(msg, session.replayReference, {
          cursor: session.replayCursor,
          done: false,
        });
        session.replayCursor = decision.next.cursor;
        session.replayDone = decision.next.done;
        if (decision.duplicate) {
          if (msg.messageId) session.seenMessageIds.push(msg.messageId);
          return;
        }
      }

      if (msg.messageId) session.seenMessageIds.push(msg.messageId);
      session.previousMessages.push(msg);
      if (state.focusedContactId !== contactId && msg.talker !== 'agent') {
        session.unreadCount += 1;
      }
      syncFocusedState(state);
    },
    /**
     * After GET /chat/init: align messageId / notes on current chat rows (e.g. after 404 on notes).
     */
    mergeChatMessageMetadataFromInit: (
      state,
      action: PayloadAction<
        CallMessage[] | { contactId?: string; messages: CallMessage[] }
      >
    ) => {
      const contactId = Array.isArray(action.payload)
        ? getTargetContactId(state)
        : getTargetContactId(state, action.payload.contactId);
      if (!contactId) return;
      const session = ensureSession(state, contactId, 'chat');
      const payloadMessages = Array.isArray(action.payload)
        ? action.payload
        : action.payload.messages;
      const server = normalizeChatInitMessages(payloadMessages ?? []);
      for (const local of session.previousMessages) {
        const match = server.find(
          s =>
            (s.messageId &&
              local.messageId &&
              s.messageId === local.messageId) ||
            (s.message === local.message &&
              s.talker === local.talker &&
              s.timestamp === local.timestamp)
        );
        if (!match) continue;
        if (match.messageId) local.messageId = match.messageId;
        if (match.notes?.length) {
          const seen = new Set((local.notes ?? []).map(n => n.note));
          const add = match.notes.filter(n => !seen.has(n.note));
          if (add.length) local.notes = [...(local.notes ?? []), ...add];
        }
      }
      syncFocusedState(state);
    },
    /** Set contact information from endpoints. */
    setContactInformation: (
      state,
      action: PayloadAction<{
        contactId?: string;
        callerName?: string;
        callerPhoneNumber?: string;
        authenticated?: boolean;
        isDirectConnect?: boolean;
        isCallback?: boolean;
      } | null>
    ) => {
      const contactId = getTargetContactId(state, action.payload?.contactId);
      const authenticated = action.payload?.authenticated;
      if (contactId) {
        const session = ensureSession(state, contactId);
        session.contactInformation = action.payload;
        if (authenticated !== undefined) {
          session.authenticated = authenticated;
        }
      }
      if (!contactId || state.focusedContactId === contactId) {
        state.contactInformation = action.payload;
        if (authenticated !== undefined) {
          state.authenticated = authenticated;
        }
      }
    },
    setFocusedContact: (state, action: PayloadAction<string | null>) => {
      state.focusedContactId = action.payload;
      if (action.payload && state.sessions[action.payload]) {
        state.sessions[action.payload].unreadCount = 0;
      }
      syncFocusedState(state);
    },
    registerContactSession: (
      state,
      action: PayloadAction<{
        contactId: string;
        type?: ContactSessionType;
        startedAt?: number | null;
        focus?: boolean;
      }>
    ) => {
      const session = ensureSession(
        state,
        action.payload.contactId,
        action.payload.type,
        action.payload.startedAt
      );
      if (action.payload.focus || !state.focusedContactId) {
        state.focusedContactId = action.payload.contactId;
        session.unreadCount = 0;
      }
      syncFocusedState(state);
    },
    removeContactSession: (
      state,
      action: PayloadAction<{ contactId: string }>
    ) => {
      const { contactId } = action.payload;
      delete state.sessions[contactId];
      state.sessionOrder = state.sessionOrder.filter(id => id !== contactId);
      if (state.voiceContactId === contactId) state.voiceContactId = null;
      if (state.focusedContactId === contactId) state.focusedContactId = null;
      syncFocusedState(state);
    },
    setSessionCustomerInfo: (
      state,
      action: PayloadAction<{
        contactId: string;
        customer: CustomerInformationResponse | null;
      }>
    ) => {
      const { contactId, customer } = action.payload;
      // Don't recreate a session that was already deleted (e.g. agent clicked
      // Finish After Call Work before an in-flight fetchCustomerForCall or
      // link-customer dispatch resolved).
      if (!state.sessions[contactId]) return;
      state.sessions[contactId].currentCustomer = customer;
      syncFocusedState(state);
    },
    setSessionCaseInfo: (
      state,
      action: PayloadAction<{
        contactId: string;
        caseInfo: CaseInformationResponse | null;
      }>
    ) => {
      const { contactId, caseInfo } = action.payload;
      // Don't recreate a session that was already deleted (e.g. agent clicked
      // Finish After Call Work while a NotesCard auto-save timer or an
      // in-flight async dispatch was still pending).
      if (!state.sessions[contactId]) return;
      state.sessions[contactId].currentCase = caseInfo;
      syncFocusedState(state);
    },
    setSessionCaseHistory: (
      state,
      action: PayloadAction<{
        contactId: string;
        caseHistory: CaseHistoryEntry[];
      }>
    ) => {
      const session = ensureSession(state, action.payload.contactId);
      session.caseHistory = action.payload.caseHistory;
      syncFocusedState(state);
    },
    clearFocusedChatUnread: state => {
      if (state.focusedContactId && state.sessions[state.focusedContactId]) {
        state.sessions[state.focusedContactId].unreadCount = 0;
      }
      syncFocusedState(state);
    },
    clearCallContext: () => initialState,
  },
});

export const {
  setCallStreamInit,
  setCallStreamLoading,
  clearCallStreamLoading,
  setDirectConnectLinkedWorkspace,
  appendTranscriptChunk,
  addSegmentNote,
  setRecordingState,
  setCustomerOnHold,
  setWarmTransferAlert,
  setContactId,
  appendChatMessage,
  mergeChatMessageMetadataFromInit,
  setContactInformation,
  setFocusedContact,
  registerContactSession,
  removeContactSession,
  setSessionCustomerInfo,
  setSessionCaseInfo,
  setSessionCaseHistory,
  clearFocusedChatUnread,
  clearCallContext,
} = callSlice.actions;
export default callSlice.reducer;
